import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Can, EmptyState, PageHeader, formatDateTime } from '@/kit/ui'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useHubStore } from '@/store/hubStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Alert } from '@/types'

const KIND_LABEL: Record<NonNullable<Alert['kind']>, string> = { new_code: 'new lender code', error_rate: 'endpoint errors', lender_off: 'lender off', info: 'notice' }

/** Alerts (F3-6): the three rules (new lender code, endpoint error rate, lender switched off) and notices; Acknowledge writes History. */
export function AlertsScreen() {
  const { user } = useCurrentUser()
  const alerts = useHubStore((s) => s.alerts)
  const lenders = useHubStore((s) => s.lenders)
  const ack = useHubStore((s) => s.acknowledgeAlert)
  const rule = useSettingsStore((s) => s.settings.errorRule)
  const [kind, setKind] = useState<'all' | NonNullable<Alert['kind']>>('all')
  const [state, setState] = useState<'open' | 'all'>('all')
  const open = alerts.filter((a) => !a.acknowledgedAt)
  const name = (slug: string | null) => lenders.find((l) => l.slug === slug)?.name ?? slug ?? '—'
  const shown = alerts.filter((a) => (kind === 'all' || (a.kind ?? 'info') === kind) && (state === 'all' || !a.acknowledgedAt))
  const count = (k: NonNullable<Alert['kind']>) => open.filter((a) => (a.kind ?? 'info') === k).length

  const RULES: [NonNullable<Alert['kind']>, string, string][] = [
    ['new_code', 'A new lender code', 'A run (test call or production) sees a lender code that parse() turns into error and that was never captured. Fix: Add as a test in 3.1, change parse(), new batch version.'],
    ['error_rate', 'Endpoint error rate', `More than ${rule.errors} errors within ${rule.minutes} minutes on one endpoint (Settings). Fix: look at Workflow runs; suspend the endpoint if the lender is down.`],
    ['lender_off', 'Lender switched off', 'Someone used the single switch; website, app and endpoints follow within the cache time. Fix: switch on again when the reason is gone.'],
  ]

  return (
    <>
      <PageHeader
        title="Alerts"
        subtitle={`${open.length} open · ${alerts.length - open.length} acknowledged`}
        actions={
          <div className="flex gap-2">
            <select className="input w-auto" aria-label="Rule" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="all">Every rule</option>
              {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select className="input w-auto" aria-label="State" value={state} onChange={(e) => setState(e.target.value as typeof state)}>
              <option value="all">Open and acknowledged</option>
              <option value="open">Open only</option>
            </select>
          </div>
        }
      />
      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="alert-rules">
        {RULES.map(([k, t, d]) => (
          <section key={k} className="card">
            <div className="mb-1 flex items-center justify-between gap-2"><h2 className="section-title !mb-0">{t}</h2><Badge tone={count(k) ? 'danger' : 'gray'}>{count(k)} open</Badge></div>
            <p className="text-2xs text-muted">{d}</p>
          </section>
        ))}
      </div>
      {shown.length === 0 ? <EmptyState title="No alerts here" /> : (
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead><tr><th>When</th><th>Lender</th><th>Rule</th><th>What</th><th>State</th><th></th></tr></thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.id} className={a.acknowledgedAt ? 'opacity-60' : ''}>
                  <td className="mono whitespace-nowrap">{formatDateTime(a.at)}</td>
                  <td>{a.lenderSlug ? <Link to={`/lenders/${a.lenderSlug}`}>{name(a.lenderSlug)}</Link> : '—'}</td>
                  <td className="whitespace-nowrap text-2xs">{KIND_LABEL[a.kind ?? 'info']}</td>
                  <td>{a.text}</td>
                  <td><Badge tone={a.acknowledgedAt ? 'gray' : a.severity === 'danger' ? 'danger' : a.severity === 'warning' ? 'warning' : 'info'}>{a.acknowledgedAt ? 'acknowledged' : 'open'}</Badge></td>
                  <td className="text-right">{!a.acknowledgedAt && <Can permission="alerts.manage"><button type="button" className="btn btn-ghost btn-sm" onClick={() => ack(a.id, user.email)}>Acknowledge</button></Can>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
